// PhotographyAbout.js - Photography-themed About section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a photography-themed About section with photographer profile and story
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the about is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The about elements
 */
export const createPhotographyAboutElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const aboutSectionClass = 'photography-about-section';
  const aboutContainerClass = 'photography-about-container';
  const aboutHeaderClass = 'photography-about-header';
  const aboutTitleClass = 'photography-about-title';
  const aboutSubtitleClass = 'photography-about-subtitle';
  const aboutContentClass = 'photography-about-content';
  const aboutLeftClass = 'photography-about-left';
  const aboutRightClass = 'photography-about-right';
  const aboutImageWrapperClass = 'photography-about-image-wrapper';
  const aboutImageClass = 'photography-about-image';
  const aboutStoryClass = 'photography-about-story';
  const aboutStoryTitleClass = 'photography-about-story-title';
  const aboutStoryTextClass = 'photography-about-story-text';
  const aboutSkillsClass = 'photography-about-skills';
  const aboutSkillsTitleClass = 'photography-about-skills-title';
  const aboutSkillsListClass = 'photography-about-skills-list';
  const aboutSkillItemClass = 'photography-about-skill-item';
  const aboutSkillNameClass = 'photography-about-skill-name';
  const aboutSkillBarClass = 'photography-about-skill-bar';
  const aboutSkillFillClass = 'photography-about-skill-fill';
  const aboutQuoteClass = 'photography-about-quote';
  const aboutQuoteTextClass = 'photography-about-quote-text';
  const aboutQuoteAuthorClass = 'photography-about-quote-author';

  // Desktop Styles
  const aboutSectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#0f0f0f',
    background: `
      radial-gradient(circle at 80% 20%, rgba(212, 175, 55, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 20% 80%, rgba(212, 175, 55, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 50%, #0f0f0f 100%)
    `,
    color: '#f5f5f5',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden'
  };

  const aboutContainerStyles = {
    maxWidth: '1400px',
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '80px',
    position: 'relative',
    zIndex: '1'
  };

  const aboutHeaderStyles = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const aboutTitleStyles = {
    fontSize: '56px',
    fontWeight: '300',
    color: '#f5f5f5',
    marginBottom: '24px',
    letterSpacing: '-0.02em',
    fontFamily: '"Playfair Display", serif'
  };

  const aboutSubtitleStyles = {
    fontSize: '20px',
    fontWeight: '300',
    color: '#c0c0c0',
    lineHeight: '1.6',
    maxWidth: '600px',
    margin: '0 auto',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutContentStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'start'
  };

  const aboutLeftStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    background: `
      linear-gradient(135deg, rgba(212, 175, 55, 0.08) 0%, rgba(212, 175, 55, 0.04) 100%),
      rgba(26, 26, 26, 0.6)
    `,
    padding: '40px',
    borderRadius: '0',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    backdropFilter: 'blur(10px)'
  };

  const aboutRightStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px'
  };

  const aboutImageWrapperStyles = {
    position: 'relative',
    borderRadius: '0',
    overflow: 'hidden',
    boxShadow: `
      0 20px 40px -8px rgba(0, 0, 0, 0.6),
      0 0 0 1px rgba(212, 175, 55, 0.3),
      0 0 30px rgba(212, 175, 55, 0.1)
    `,
    border: '2px solid #d4af37',
    alignSelf: 'center',
    maxWidth: '300px',
    width: '100%'
  };

  const aboutImageStyles = {
    width: '100%',
    height: '350px',
    objectFit: 'cover',
    transition: 'all 0.6s ease',
    filter: 'contrast(1.1) brightness(1.05)'
  };

  const aboutStoryStyles = {
    background: `
      linear-gradient(135deg, rgba(212, 175, 55, 0.08) 0%, rgba(212, 175, 55, 0.04) 100%),
      rgba(26, 26, 26, 0.6)
    `,
    padding: '40px',
    borderRadius: '0',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    backdropFilter: 'blur(10px)'
  };

  const aboutStoryTitleStyles = {
    fontSize: '32px',
    fontWeight: '300',
    color: '#d4af37',
    marginBottom: '24px',
    fontFamily: '"Playfair Display", serif'
  };

  const aboutStoryTextStyles = {
    fontSize: '16px',
    lineHeight: '1.8',
    color: '#c0c0c0',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutSkillsStyles = {
    background: 'transparent',
    padding: '0',
    borderRadius: '0',
    border: 'none',
    backdropFilter: 'none'
  };

  const aboutSkillsTitleStyles = {
    fontSize: '28px',
    fontWeight: '300',
    color: '#d4af37',
    marginBottom: '32px',
    fontFamily: '"Playfair Display", serif'
  };

  const aboutSkillsListStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  };

  const aboutSkillItemStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const aboutSkillNameStyles = {
    fontSize: '16px',
    fontWeight: '500',
    color: '#f5f5f5',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutSkillBarStyles = {
    width: '100%',
    height: '6px',
    backgroundColor: 'rgba(212, 175, 55, 0.2)',
    borderRadius: '3px',
    overflow: 'hidden'
  };

  const aboutSkillFillStyles = {
    height: '100%',
    background: 'linear-gradient(90deg, #d4af37 0%, #b8941f 100%)',
    borderRadius: '3px',
    transition: 'width 1.5s ease'
  };

  const aboutQuoteStyles = {
    textAlign: 'center',
    padding: '60px 40px',
    background: `
      linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0.05) 100%),
      rgba(26, 26, 26, 0.8)
    `,
    borderRadius: '0',
    border: '1px solid rgba(212, 175, 55, 0.25)',
    backdropFilter: 'blur(20px)',
    marginTop: '40px'
  };

  const aboutQuoteTextStyles = {
    fontSize: '24px',
    fontWeight: '300',
    color: '#f5f5f5',
    lineHeight: '1.6',
    fontStyle: 'italic',
    marginBottom: '24px',
    fontFamily: '"Playfair Display", serif'
  };

  const aboutQuoteAuthorStyles = {
    fontSize: '16px',
    color: '#d4af37',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: '"Inter", sans-serif'
  };

  // Tablet Styles
  const tabletAboutContentStyles = {
    gridTemplateColumns: '1fr',
    gap: '60px'
  };

  const tabletAboutTitleStyles = {
    fontSize: '48px'
  };

  const tabletAboutImageStyles = {
    height: '500px'
  };

  // Mobile Styles
  const mobileAboutSectionStyles = {
    paddingTop: '80px',
    paddingBottom: '80px'
  };

  const mobileAboutTitleStyles = {
    fontSize: '36px'
  };

  const mobileAboutSubtitleStyles = {
    fontSize: '18px'
  };

  const mobileAboutStoryStyles = {
    padding: '30px 20px'
  };

  const mobileAboutLeftStyles = {
    padding: '30px 20px'
  };

  const mobileAboutSkillsStyles = {
    padding: '0'
  };

  const mobileAboutQuoteStyles = {
    padding: '40px 20px'
  };

  const mobileAboutQuoteTextStyles = {
    fontSize: '20px'
  };

  // Register styles
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(aboutSectionClass, aboutSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutContainerClass, aboutContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutHeaderClass, aboutHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutTitleClass, aboutTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutSubtitleClass, aboutSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutContentClass, aboutContentStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutLeftClass, aboutLeftStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutRightClass, aboutRightStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutImageWrapperClass, aboutImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutImageClass, aboutImageStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutStoryClass, aboutStoryStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutStoryTitleClass, aboutStoryTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutStoryTextClass, aboutStoryTextStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutSkillsClass, aboutSkillsStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutSkillsTitleClass, aboutSkillsTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutSkillsListClass, aboutSkillsListStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutSkillItemClass, aboutSkillItemStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutSkillNameClass, aboutSkillNameStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutSkillBarClass, aboutSkillBarStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutSkillFillClass, aboutSkillFillStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutQuoteClass, aboutQuoteStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutQuoteTextClass, aboutQuoteTextStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutQuoteAuthorClass, aboutQuoteAuthorStyles);
    
    // Tablet styles
    newRegistry[DEVICES.TABLET].set(aboutContentClass, tabletAboutContentStyles);
    newRegistry[DEVICES.TABLET].set(aboutTitleClass, tabletAboutTitleStyles);
    newRegistry[DEVICES.TABLET].set(aboutImageClass, tabletAboutImageStyles);
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(aboutSectionClass, mobileAboutSectionStyles);
    newRegistry[DEVICES.MOBILE].set(aboutTitleClass, mobileAboutTitleStyles);
    newRegistry[DEVICES.MOBILE].set(aboutSubtitleClass, mobileAboutSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(aboutLeftClass, mobileAboutLeftStyles);
    newRegistry[DEVICES.MOBILE].set(aboutStoryClass, mobileAboutStoryStyles);
    newRegistry[DEVICES.MOBILE].set(aboutSkillsClass, mobileAboutSkillsStyles);
    newRegistry[DEVICES.MOBILE].set(aboutQuoteClass, mobileAboutQuoteStyles);
    newRegistry[DEVICES.MOBILE].set(aboutQuoteTextClass, mobileAboutQuoteTextStyles);
    
    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    aboutSectionClass, aboutContainerClass, aboutHeaderClass, aboutTitleClass,
    aboutSubtitleClass, aboutContentClass, aboutLeftClass, aboutRightClass,
    aboutImageWrapperClass, aboutImageClass, aboutStoryClass, aboutStoryTitleClass,
    aboutStoryTextClass, aboutSkillsClass, aboutSkillsTitleClass, aboutSkillsListClass,
    aboutSkillItemClass, aboutSkillNameClass, aboutSkillBarClass, aboutSkillFillClass,
    aboutQuoteClass, aboutQuoteTextClass, aboutQuoteAuthorClass
  ]));

  // Generate IDs for all elements
  const aboutSectionId = Date.now();
  const containerId = aboutSectionId + 1;
  const headerId = aboutSectionId + 2;
  const titleId = aboutSectionId + 3;
  const subtitleId = aboutSectionId + 4;
  const contentId = aboutSectionId + 5;
  const leftId = aboutSectionId + 6;
  const rightId = aboutSectionId + 7;
  const imageWrapperId = aboutSectionId + 8;
  const imageId = aboutSectionId + 9;
  const storyId = aboutSectionId + 10;
  const storyTitleId = aboutSectionId + 11;
  const storyTextId = aboutSectionId + 12;
  const skillsId = aboutSectionId + 13;
  const skillsTitleId = aboutSectionId + 14;
  const skillsListId = aboutSectionId + 15;
  const skill1Id = aboutSectionId + 16;
  const skill1NameId = aboutSectionId + 17;
  const skill1BarId = aboutSectionId + 18;
  const skill1FillId = aboutSectionId + 19;
  const skill2Id = aboutSectionId + 20;
  const skill2NameId = aboutSectionId + 21;
  const skill2BarId = aboutSectionId + 22;
  const skill2FillId = aboutSectionId + 23;
  const skill3Id = aboutSectionId + 24;
  const skill3NameId = aboutSectionId + 25;
  const skill3BarId = aboutSectionId + 26;
  const skill3FillId = aboutSectionId + 27;
  const skill4Id = aboutSectionId + 28;
  const skill4NameId = aboutSectionId + 29;
  const skill4BarId = aboutSectionId + 30;
  const skill4FillId = aboutSectionId + 31;
  const quoteId = aboutSectionId + 32;
  const quoteTextId = aboutSectionId + 33;
  const quoteAuthorId = aboutSectionId + 34;

  const elements = [
    // Main About Section
    {
      id: aboutSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Photography About Section',
      parentId: null,
      className: aboutSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'photography-about-section',
        'data-component': 'section',
        'data-section-type': 'about',
        'data-page-id': currentPageId
      }
    },

    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Container',
      parentId: aboutSectionId,
      hierarchyName: getHierarchyName('div'),
      className: aboutContainerClass,
      styles: {},
      children: [headerId, contentId, quoteId],
      text: '',
      attributes: {
        'data-element': 'about-container'
      }
    },

    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: aboutHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'about-header'
      }
    },

    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'About Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: aboutTitleClass,
      styles: {},
      children: [],
      text: 'About the Artist',
      attributes: {
        'data-element': 'about-title'
      }
    },

    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'About Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: aboutSubtitleClass,
      styles: {},
      children: [],
      text: 'Passionate about capturing life\'s most beautiful moments through the lens of creativity and emotion.',
      attributes: {
        'data-element': 'about-subtitle'
      }
    },

    // Content
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: aboutContentClass,
      styles: {},
      children: [leftId, rightId],
      text: '',
      attributes: {
        'data-element': 'about-content'
      }
    },

    // Left Column
    {
      id: leftId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Left',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: aboutLeftClass,
      styles: {},
      children: [imageWrapperId, skillsId],
      text: '',
      attributes: {
        'data-element': 'about-left'
      }
    },

    // Right Column
    {
      id: rightId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Right',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: aboutRightClass,
      styles: {},
      children: [storyId],
      text: '',
      attributes: {
        'data-element': 'about-right'
      }
    },

    // Image Wrapper
    {
      id: imageWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Image Wrapper',
      parentId: leftId,
      hierarchyName: getHierarchyName('div'),
      className: aboutImageWrapperClass,
      styles: {},
      children: [imageId],
      text: '',
      attributes: {
        'data-element': 'about-image-wrapper'
      }
    },

    // About Image
    {
      id: imageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'About Image',
      parentId: imageWrapperId,
      hierarchyName: getHierarchyName('img'),
      className: aboutImageClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      attributes: {
        'data-element': 'about-image',
        'alt': 'Professional photographer portrait'
      }
    },

    // Story Section
    {
      id: storyId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Story',
      parentId: rightId,
      hierarchyName: getHierarchyName('div'),
      className: aboutStoryClass,
      styles: {},
      children: [storyTitleId, storyTextId],
      text: '',
      attributes: {
        'data-element': 'about-story'
      }
    },

    // Story Title
    {
      id: storyTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Story Title',
      parentId: storyId,
      hierarchyName: getHierarchyName('h3'),
      className: aboutStoryTitleClass,
      styles: {},
      children: [],
      text: 'My Journey',
      attributes: {
        'data-element': 'about-story-title'
      }
    },

    // Story Text
    {
      id: storyTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Story Text',
      parentId: storyId,
      hierarchyName: getHierarchyName('p'),
      className: aboutStoryTextClass,
      styles: {},
      children: [],
      text: 'With over 8 years of experience in professional photography, I have dedicated my career to capturing the essence of human emotion and natural beauty. My journey began with a simple camera and an insatiable curiosity about the world around me.\n\nFrom intimate wedding ceremonies to grand corporate events, I believe every moment has a story worth telling. My approach combines technical expertise with artistic vision, ensuring that each photograph not only documents a moment but transforms it into a timeless piece of art.\n\nI specialize in creating authentic, emotional imagery that resonates with viewers long after the moment has passed. Whether it\'s the gentle touch between newlyweds or the candid laughter of children, I strive to capture the genuine connections that make life beautiful.',
      attributes: {
        'data-element': 'about-story-text'
      }
    },

    // Skills Section
    {
      id: skillsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Skills',
      parentId: leftId,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillsClass,
      styles: {},
      children: [skillsTitleId, skillsListId],
      text: '',
      attributes: {
        'data-element': 'about-skills'
      }
    },

    // Skills Title
    {
      id: skillsTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Skills Title',
      parentId: skillsId,
      hierarchyName: getHierarchyName('h3'),
      className: aboutSkillsTitleClass,
      styles: {},
      children: [],
      text: 'Expertise',
      attributes: {
        'data-element': 'about-skills-title'
      }
    },

    // Skills List
    {
      id: skillsListId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skills List',
      parentId: skillsId,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillsListClass,
      styles: {},
      children: [skill1Id, skill2Id, skill3Id, skill4Id],
      text: '',
      attributes: {
        'data-element': 'about-skills-list'
      }
    },

    // Skill 1 - Wedding Photography
    {
      id: skill1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 1',
      parentId: skillsListId,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillItemClass,
      styles: {},
      children: [skill1NameId, skill1BarId],
      text: '',
      attributes: {
        'data-element': 'about-skill-item'
      }
    },

    {
      id: skill1NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 1 Name',
      parentId: skill1Id,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillNameClass,
      styles: {},
      children: [],
      text: 'Wedding Photography',
      attributes: {
        'data-element': 'about-skill-name'
      }
    },

    {
      id: skill1BarId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 1 Bar',
      parentId: skill1Id,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillBarClass,
      styles: {},
      children: [skill1FillId],
      text: '',
      attributes: {
        'data-element': 'about-skill-bar'
      }
    },

    {
      id: skill1FillId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 1 Fill',
      parentId: skill1BarId,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillFillClass,
      styles: { width: '95%' },
      children: [],
      text: '',
      attributes: {
        'data-element': 'about-skill-fill'
      }
    },

    // Skill 2 - Portrait Photography
    {
      id: skill2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 2',
      parentId: skillsListId,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillItemClass,
      styles: {},
      children: [skill2NameId, skill2BarId],
      text: '',
      attributes: {
        'data-element': 'about-skill-item'
      }
    },

    {
      id: skill2NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 2 Name',
      parentId: skill2Id,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillNameClass,
      styles: {},
      children: [],
      text: 'Portrait Photography',
      attributes: {
        'data-element': 'about-skill-name'
      }
    },

    {
      id: skill2BarId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 2 Bar',
      parentId: skill2Id,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillBarClass,
      styles: {},
      children: [skill2FillId],
      text: '',
      attributes: {
        'data-element': 'about-skill-bar'
      }
    },

    {
      id: skill2FillId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 2 Fill',
      parentId: skill2BarId,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillFillClass,
      styles: { width: '90%' },
      children: [],
      text: '',
      attributes: {
        'data-element': 'about-skill-fill'
      }
    },

    // Skill 3 - Event Photography
    {
      id: skill3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 3',
      parentId: skillsListId,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillItemClass,
      styles: {},
      children: [skill3NameId, skill3BarId],
      text: '',
      attributes: {
        'data-element': 'about-skill-item'
      }
    },

    {
      id: skill3NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 3 Name',
      parentId: skill3Id,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillNameClass,
      styles: {},
      children: [],
      text: 'Event Photography',
      attributes: {
        'data-element': 'about-skill-name'
      }
    },

    {
      id: skill3BarId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 3 Bar',
      parentId: skill3Id,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillBarClass,
      styles: {},
      children: [skill3FillId],
      text: '',
      attributes: {
        'data-element': 'about-skill-bar'
      }
    },

    {
      id: skill3FillId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 3 Fill',
      parentId: skill3BarId,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillFillClass,
      styles: { width: '88%' },
      children: [],
      text: '',
      attributes: {
        'data-element': 'about-skill-fill'
      }
    },

    // Skill 4 - Photo Editing
    {
      id: skill4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 4',
      parentId: skillsListId,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillItemClass,
      styles: {},
      children: [skill4NameId, skill4BarId],
      text: '',
      attributes: {
        'data-element': 'about-skill-item'
      }
    },

    {
      id: skill4NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 4 Name',
      parentId: skill4Id,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillNameClass,
      styles: {},
      children: [],
      text: 'Photo Editing & Retouching',
      attributes: {
        'data-element': 'about-skill-name'
      }
    },

    {
      id: skill4BarId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 4 Bar',
      parentId: skill4Id,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillBarClass,
      styles: {},
      children: [skill4FillId],
      text: '',
      attributes: {
        'data-element': 'about-skill-bar'
      }
    },

    {
      id: skill4FillId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Skill 4 Fill',
      parentId: skill4BarId,
      hierarchyName: getHierarchyName('div'),
      className: aboutSkillFillClass,
      styles: { width: '92%' },
      children: [],
      text: '',
      attributes: {
        'data-element': 'about-skill-fill'
      }
    },

    // Quote Section
    {
      id: quoteId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Quote',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: aboutQuoteClass,
      styles: {},
      children: [quoteTextId, quoteAuthorId],
      text: '',
      attributes: {
        'data-element': 'about-quote'
      }
    },

    // Quote Text
    {
      id: quoteTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'blockquote',
      name: 'Quote Text',
      parentId: quoteId,
      hierarchyName: getHierarchyName('blockquote'),
      className: aboutQuoteTextClass,
      styles: {},
      children: [],
      text: '"Photography is not just about capturing what you see, but about revealing what you feel. Every image tells a story, and every story deserves to be told beautifully."',
      attributes: {
        'data-element': 'about-quote-text'
      }
    },

    // Quote Author
    {
      id: quoteAuthorId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'cite',
      name: 'Quote Author',
      parentId: quoteId,
      hierarchyName: getHierarchyName('cite'),
      className: aboutQuoteAuthorClass,
      styles: {},
      children: [],
      text: '— Sarah Mitchell, Lead Photographer',
      attributes: {
        'data-element': 'about-quote-author'
      }
    }
  ];

  return {
    aboutSection: elements[0],
    elements: elements
  };
}; 